import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/providers/AuthProvider';
import { ProgressBar } from './ProgressBar';
import { OnboardingTour } from './Tour';
import { CnpjStep } from './steps/CnpjStep';
import { EmailStep } from './steps/EmailStep';
import { ProfileStep } from './steps/ProfileStep';
import {
  ONBOARDING_STEPS,
  deriveCompletedFromUser,
  firstIncompleteStep,
  stepIndex,
  useWizardState,
  type OnboardingStep,
  type WizardState,
} from './wizardState';

export default function OnboardingPage() {
  const { user, isAuthenticated, isBootstrapping, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { state, update, markCompleted, goTo, reset } = useWizardState();
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (!isBootstrapping && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isBootstrapping, isAuthenticated, navigate]);

  useEffect(() => {
    if (!user) return;
    const derivedCompleted = deriveCompletedFromUser(user);
    for (const step of ONBOARDING_STEPS) {
      if (derivedCompleted[step] && !state.completed[step]) {
        markCompleted(step);
      }
    }
    const target = firstIncompleteStep({ ...state.completed, ...derivedCompleted });
    if (stepIndex(target) > stepIndex(state.currentStep)) {
      goTo(target);
    }
  }, [user, state.completed, state.currentStep, markCompleted, goTo]);

  const handleStepComplete = (step: OnboardingStep) => (patch: Partial<WizardState>) => {
    update(patch);
    markCompleted(step);
    const idx = ONBOARDING_STEPS.indexOf(step);
    const next = ONBOARDING_STEPS[idx + 1];
    if (next) {
      goTo(next);
    } else {
      void refreshUser().finally(() => setShowTour(true));
    }
  };

  const goBackTo = (step: OnboardingStep) => () => goTo(step);

  if (isBootstrapping || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-(--color-fg-muted)">
        Carregando…
      </div>
    );
  }

  if (showTour) {
    return (
      <div className="min-h-screen bg-(--color-bg) text-(--color-fg)">
        <OnboardingTour onFinish={reset} />
      </div>
    );
  }

  const step = state.currentStep;

  return (
    <div className="min-h-screen bg-(--color-bg) text-(--color-fg)">
      <div className="mx-auto max-w-xl px-4 py-10">
        <header className="mb-6">
          <p className="text-xs font-medium tracking-wide text-(--color-accent) uppercase">
            Primeiros passos
          </p>
          <h1 className="font-display text-2xl font-semibold">Vamos configurar sua conta</h1>
        </header>

        <ProgressBar current={step} />

        {step === 'email' ? (
          <EmailStep onComplete={handleStepComplete('email')} />
        ) : null}
        {step === 'profile' ? (
          <ProfileStep state={state} onComplete={handleStepComplete('profile')} />
        ) : null}
        {step === 'cnpj' ? (
          <CnpjStep
            state={state}
            onComplete={handleStepComplete('cnpj')}
            onBack={goBackTo('profile')}
          />
        ) : null}
      </div>
    </div>
  );
}
